import { FC } from "react";
import { Button, Space, Tooltip, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { SvgIcon } from "../../../../../common/components/SvgIcon";
import { formatNumberWithSuffix } from "../../../../../common/Number";
import { ScoreTag } from "../../../../../common/components/ScoreTag";
import { ExpirationDateCell } from "../../../../../common/components/ExpirationDateCell";
import { ListCard, ListCardField, ListCardTag } from "../../../../../common/components/ListCard";
import { OrderForm } from "../../../../../types/OrderFormTypes";

const { Text } = Typography;

interface DealCardProps {
    deal: OrderForm;
}

export const DealCard: FC<DealCardProps> = ({ deal }) => {
    const navigate = useNavigate();
    const vendorName = deal.vendor?.name || deal.vendorKey || "";

    const dealPath = deal.projectId ? `/project/${deal.projectId}` : `/order/${deal.id}`;

    const fields: ListCardField[] = [
        {
            label: "Deal",
            span: 2,
            content: (
                <Text strong ellipsis style={{ fontSize: 14, color: "#1890FF" }}>
                    {vendorName}
                </Text>
            ),
        },
        {
            label: "Deal score",
            span: 2,
            content: <ScoreTag score={deal.orderScore} type="percentage" showGrade />,
        },
        {
            label: "Deal value",
            span: 2,
            content: <ListCardTag color="grey">${formatNumberWithSuffix(deal.value)}</ListCardTag>,
        },
        {
            label: "Estimated savings",
            span: 2,
            content: (
                <ListCardTag color="green">
                    ${formatNumberWithSuffix(Number(deal.decrease || 0))}
                </ListCardTag>
            ),
        },
        {
            label: "Expiration",
            span: 2,
            content: (
                <ExpirationDateCell date={deal.orderEndDate?.toString()} orderFormId={deal.id} />
            ),
        },
        {
            label: "Top negotiation strategies",
            span: 2,
            content: (
                <div>
                    <Text ellipsis>{deal.topNegotiationStrategies || "—"}</Text>
                    <div>
                        <Link to={dealPath} onClick={(e) => e.stopPropagation()}>
                            View more
                        </Link>
                    </div>
                </div>
            ),
        },
    ];

    const actions = (
        <Space size={8}>
            <Tooltip title="Project">
                <Link to={dealPath}>
                    <Button type="primary" icon={<SvgIcon name="forms" color="#fff" size={14} />} />
                </Link>
            </Tooltip>
            {deal.vendor?.supplier?.id ? (
                <Tooltip title="Buyer Brief">
                    <Link to={`/supplier/${deal.vendor?.supplier?.id}`}>
                        <Button
                            type="primary"
                            icon={<SvgIcon name="briefs" color="#fff" size={14} />}
                        />
                    </Link>
                </Tooltip>
            ) : (
                <Tooltip title="Buyer Brief">
                    <Button disabled icon={<SvgIcon name="briefs" size={14} />} />
                </Tooltip>
            )}
            <Tooltip title="Deal Score">
                <Link to={`/order/score/${deal.id}`}>
                    <Button type="primary" icon={<SvgIcon name="group" color="#fff" size={14} />} />
                </Link>
            </Tooltip>
        </Space>
    );

    return (
        <ListCard
            style={{ background: "#FAFAFA", borderBottom: "1px solid #D9D9D9" }}
            onClick={() => navigate(dealPath)}
            logo={deal.vendor?.hero ? <img src={deal.vendor.hero} alt={vendorName} /> : undefined}
            logoFallback={vendorName || deal.vendorKey || "?"}
            fields={fields}
            actions={actions}
            columns={14}
        />
    );
};
